import Button from "components/common/button/Button";
import Input from "components/common/form/inputs/Input";
import TelInput from "components/common/form/inputs/TelInput";
import Translate from "components/common/Translate";
import { uuid } from "config/app-constants";
import { graph, GraphResponse } from "config/axios-context";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SavePersonMutation, PersonInput, SavePersonMutationVariables, PersonQuery, PersonContactInfoInput, SavePersonDocument } from "types/graphql";
import { ContactType, getPersonData, PersonQueryResponseToPersonType, PersonType } from "types/person-types";
import { useContext } from "react";
import { getAppContext } from 'config/AppContext';
import Loading from "components/common/loading/Loading";
import Textarea from "components/common/form/inputs/Textarea";
import FormActions from "components/common/form/partials/FormActions";
import { CoreFormPropsType } from "types/form-types";
import TagInput from "components/entities/app/person/partials/inputs/TagsInput";
import Form from "components/common/form/Form";

// #region types
export type PersonFromPropsType = CoreFormPropsType<PersonType, SavePersonMutation>

// #endregion types

const PersonFrom = (props: PersonFromPropsType) => {
    const [t] = useTranslation();
    const context = getAppContext(useContext);
    const [model, setModel] = useState<PersonType>({
        id: 0,
        name: '',
        contactInfo: [{
            id: uuid(),
            type: 'tel',
            value: ''
        }, {
            id: uuid(),
            type: 'email',
            value: ''
        }],
        tags: [],
        ...(typeof props.model == 'object' ? props.model : {})
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const id = Number(props.model)
        if (id) {
            loadData(id).then();
        }
    }, [props.model]);

    const loadData = async (id: number) => {
        setLoading(true);
        try {
            const response = await getPersonData(id);
            if(response.success && response?.data?.person) {
                const currentModel = PersonQueryResponseToPersonType(response);
                setModel(currentModel!);
            }
        } catch(error: any) {
            console.error(error);
            context.notify('error', error.toString());
        } finally {
            setLoading(false);
        }
    }

    // #region init
    
    // #endregion init

    // #region contact
    const changeContactValue = (index: number, value: string) => {
        const currentModel = { ...model };
        model.contactInfo[index].value = value;
        setModel(currentModel);
    }

    const renderContactInputs = () => {
        if (!model.contactInfo?.length) return <></>;

        const contacts = model.contactInfo.map((c, i) => {
            if (c.type === 'email') {
                return (
                    <Input
                        name={`value-${i}`}
                        key={c.id}
                        label={`Contact ${c.type}`}
                        type={c.type}
                        minLength={4}
                        maxLength={128}
                        onChange={v => changeContactValue(i, v || '')}
                        value={c.value}
                    />
                )
            } else {
                return (
                    <TelInput
                        name={`value-${i}`}
                        key={c.id}
                        label={`Contact ${c.type}`}
                        minLength={8}
                        maxLength={128}
                        onChange={v => changeContactValue(i, v || '')}
                        value={c.value}
                    />
                )
            }
        });

        return <>{contacts}</>;
    }

    const addContact = (type: ContactType) => {
        model.contactInfo.push({
            id: uuid(),
            type,
            value: ''
        });

        setModel({
            ...model
        });
    };
    // #endregion contact

    const onSubmit = async (e: React.FormEvent) => {
        const contactInfos = model.contactInfo.filter(c => c.value).map(c => {
            return {
                name: c.type,
                value: c.value,
                link: c.link,
                id: Number(c.id) || 0
            } as PersonContactInfoInput
        });
        const personInput: PersonInput = {
            id: model.id,
            name: model.name,
            note: model.note,
            contactInfo: contactInfos.map(c => {
                return {
                    ...c,
                    personId: model.id
                }
            }),
            tags: model.tags?.map(t => {
                return {
                    id: Number(t.id) || 0,
                    tagId: Number(t.tagId),
                    personId: model.id
                }
            }) || undefined
        }

        const response = await graph<SavePersonMutation, SavePersonMutationVariables>({
            query: SavePersonDocument,
            variables: {
                person: personInput
            }
        });

        if (response.success) {
            const personType = PersonQueryResponseToPersonType(response as GraphResponse<PersonQuery>)!;
            props.onSubmit(personType, response);
        } else {
            const message = response.errors && response.errors.length ? response.errors[0].message : t('errors.saving-error');
            context.notify('error', message);
            // props.onSubmit(response.data?.person, response);
        }
    }

    const renderForm = () => {
        return (
            <Form onSubmit={onSubmit} name={`${(props.name || '')}-person-form`} id={props.formId}>
                <fieldset>
                    <Input
                        name="name"
                        autoFocus
                        autofill="name"
                        label="fields.name.name"
                        maxLength={128}
                        minLength={2}
                        required
                        value={model.name}
                        onChange={v => setModel({ ...model, name: v || '' })}
                    />
                    {renderContactInputs()}
                </fieldset>
                <fieldset className="flex gap-4">
                    <Button onClick={() => addContact('tel')} title="button.add-new" theme="link">
                        <span>+ <Translate i18nKey={'contact.tel'} /></span>
                    </Button>
                    <Button onClick={() => addContact('email')} title="button.add-new" theme="link">
                        <span>+ <Translate i18nKey={'contact.email'} /></span>
                    </Button>
                </fieldset>
                <fieldset>
                    <Textarea
                        name="note"
                        autofill="none"
                        label="fields.note.name"
                        value={model.note || ''}
                        onChange={v => setModel({...model, note: v || ''})}
                    />
                </fieldset>
                <fieldset>
                    <hr />
                </fieldset>
                <fieldset>
                    <TagInput
                        value={model.tags}
                        onChange={v => setModel({...model, tags: v || []})}
                        name="tags"
                        label="fields.tags.name"
                    />
                </fieldset>
                <FormActions />
            </Form>
        );
    }

    return loading ? <Loading /> : renderForm();
}

export default PersonFrom;
